import React from 'react';
import GlassCard from '../../common/GlassCard/GlassCard';
import Button from '../../common/Button/Button';
import { useModal } from '../../../context/ModalContext';
import styles from './About.module.css';

const WHY_US = [
  {
    heading: 'Built for you, not the shelf',
    body: 'Every solution is custom-crafted around your specific needs. No templates, no shortcuts — just technology designed to fit your business perfectly.',
  },
  {
    heading: 'Fast, iterative delivery',
    body: 'We work in short cycles, shipping early and often so you see real progress at every stage — not just a finished product months down the line.',
  },
  {
    heading: 'End-to-end ownership',
    body: 'From the first wireframe to post-launch support, we own the entire process. Nothing falls through the cracks because there is no handoff.',
  },
  {
    heading: 'A genuine partnership',
    body: 'We invest in understanding your business, not just your brief. When you succeed, we succeed — and we take that seriously.',
  },
];

const TEAM = [
  {
    image: '/images/headshots/HEADSHOT%20BREN.png',
    name: 'Brendan Campbell',
    role: 'Co-Founder & Developer',
    bio: 'Passionate about building elegant, high-performance software and turning complex problems into intuitive digital experiences.',
  },
  {
    image: '/images/headshots/HEADSHOT%20JUSTIN.png',
    name: 'Justin Benjamin',
    role: 'Co-Founder & Developer',
    bio: 'Driven by a love for clean architecture and smart automation, Justin brings a sharp technical mind and an eye for detail to every project.',
  },
];


const About = () => {
  const { openModal } = useModal();

  return (
    <>
      {/* ── Hero Statement ─────────────────────────────────────── */}
      <section id="about" className={styles.heroSection}>
        <div className={styles.heroBg} />
        <div className="section-label">About Kilonova</div>
        <div className={styles.heroContent}>
          <h2 className={styles.heroHeadline}>
            Empowering businesses<br />
            through technology that<br />
            just works.
          </h2>
          <div className={styles.heroParagraphs}>
            <p className={styles.heroSub}>
              Kilonova was born out of a shared frustration: too many businesses were being underserved by generic software that didn't quite fit. We started building custom solutions to solve every day problems for ourselves, and people we knew. We quickly realised there was a real need for a team that genuinely cared about the outcome, not just the deliverable.
            </p>
            <p className={styles.heroSub}>
              Both of us came to software through curiosity and a love of problem-solving. We live and breathe technology. Not as a profession, but as a way of thinking. That passion shapes everything we build, and it's what drives us to go the extra mile on every project we take on.
            </p>
          </div>
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNumber}>50+</span>
              <span className={styles.heroStatLabel}>Projects delivered</span>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNumber}>95%</span>
              <span className={styles.heroStatLabel}>Client satisfaction</span>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNumber}>2</span>
              <span className={styles.heroStatLabel}>Founders, fully invested</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ──────────────────────────────────────── */}
      <section className={styles.whySection}>
        <div className="section-label">Why Kilonova</div>
        <div className={styles.whyGrid}>
          {WHY_US.map((item, i) => (
            <GlassCard key={i} padding="lg" hover className={styles.whyCard}>
              <div className={styles.whyNumber}>{String(i + 1).padStart(2, '0')}</div>
              <h4 className={styles.whyHeading}>{item.heading}</h4>
              <p className={styles.whyBody}>{item.body}</p>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* ── Team ───────────────────────────────────────────────── */}
      <section className={styles.teamSection}>
        <div className="section-label">The Team</div>
        <div className={styles.teamGrid}>
          {TEAM.map((member, i) => (
            <div key={i} className={styles.teamCard}>
              <img src={member.image} alt={member.name} className={styles.avatar} />
              <h3 className={styles.memberName}>{member.name}</h3>
              <p className={styles.memberRole}>{member.role}</p>
              <p className={styles.memberBio}>{member.bio}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Full-width CTA ─────────────────────────────────────── */}
      <section
        className={styles.ctaSection}
        style={{ backgroundImage: 'url("/images/stock%20images/Wood%20Hello.png")' }}
      >
        <div className={styles.ctaFooter}>
          <Button variant="primary" size="lg" onClick={openModal}>
            Start Your Project →
          </Button>
        </div>
      </section>
    </>
  );
};

export default About;
